import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Calendar, PlayCircle, CheckCircle2, Camera, Star, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import TeamChat from "@/components/TeamChat";
import JobPhotoDocumentation from "@/components/JobPhotoDocumentation";
import CleanerTripControls from "@/components/CleanerTripControls";

const STATUS_STYLES = {
  matched: "bg-blue-50 text-blue-700 border-blue-200",
  active: "bg-teal-50 text-teal-700 border-teal-200",
  in_progress: "bg-teal-50 text-teal-700 border-teal-200",
  completed: "bg-slate-100 text-slate-700 border-slate-200",
  cancelled_by_cleaner: "bg-orange-50 text-orange-700 border-orange-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

export default function CleanerSchedule() {
  const nav = useNavigate();
  const { user } = useApp();
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoDialog, setPhotoDialog] = useState(null);
  const [photoDocReady, setPhotoDocReady] = useState(false);

  const load = async () => {
    try {
      const m = await api.get("/jobs/mine"); setMine(m.data);
    } catch (_) { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const openBefore = (id) => { setPhotoDocReady(false); setPhotoDialog({ jobId: id, phase: "before" }); };
  const openAfter  = (id) => { setPhotoDocReady(false); setPhotoDialog({ jobId: id, phase: "after"  }); };

  const startJob = async () => {
    if (!photoDialog) return;
    try {
      await api.post(`/jobs/${photoDialog.jobId}/start`);
      toast.success("Clean started");
      setPhotoDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Could not start"); }
  };
  const completeJob = async () => {
    if (!photoDialog) return;
    try {
      await api.post(`/jobs/${photoDialog.jobId}/complete`, {});
      toast.success("Payout released — 80% queued to your Stripe account");
      setPhotoDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Could not complete"); }
  };

  if (loading) return <div className="max-w-3xl mx-auto p-12 text-slate-500" data-testid="schedule-loading">Loading…</div>;

  const matched   = mine.filter(j => j.status === "matched");
  const active    = mine.filter(j => ["active", "in_progress"].includes(j.status));
  const done      = mine.filter(j => j.status === "completed");
  const cancelled = mine.filter(j => ["cancelled", "cancelled_by_cleaner"].includes(j.status));

  const AddressLine = ({ j }) => (
    <div className="text-xs text-slate-500 mt-1">
      <MapPin className="w-3 h-3 inline" />{" "}
      {j.street_address
        ? `${j.street_address}${j.unit ? ", " + j.unit : ""}, ${[j.city, j.state, j.postal_code].filter(Boolean).join(", ")}`
        : `${j.postal_code}, ${j.country}`}
    </div>
  );

  const renderRow = (j, { showActions }) => (
    <Card key={j.id} className="mb-4 border-slate-200" data-testid={`schedule-card-${j.id}`}>
      <CardContent className="p-6">
        <div className="grid md:grid-cols-4 gap-4 items-center">
          <div>
            <Badge className={`${STATUS_STYLES[j.status]} border font-mono text-[10px] uppercase`}>{j.status.replace(/_/g, " ")}</Badge>
            <div className="mt-2 font-display font-bold">{j.bedrooms}b/{j.bathrooms}ba{j.deep_clean && " · Deep"}</div>
            <AddressLine j={j} />
          </div>
          <div>
            <div className="text-xs font-mono uppercase text-slate-500">Scheduled</div>
            <div className="text-sm mt-1">{new Date(j.scheduled_at).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-xs font-mono uppercase text-slate-500">Your payout</div>
            <div className="font-display font-bold text-xl text-teal-600 mt-1">${(j.cleaner_payout / (j.team_size || 1)).toFixed(2)}</div>
          </div>
          <div className="flex flex-col gap-2">
            {showActions && j.status === "matched" && (
              <>
                <CleanerTripControls job={j} onChanged={load} />
                <Button onClick={() => openBefore(j.id)} data-testid={`start-btn-${j.id}`} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <PlayCircle className="w-4 h-4 mr-1" />Start Cleaning
                </Button>
              </>
            )}
            {showActions && (j.status === "active" || j.status === "in_progress") && (
              <Button onClick={() => openAfter(j.id)} data-testid={`complete-btn-${j.id}`} className="bg-slate-900 hover:bg-slate-800 text-white">
                <Camera className="w-4 h-4 mr-1" />Complete + Upload
              </Button>
            )}
            {j.status === "completed" && j.review && (
              <div className="flex items-center gap-1 justify-end" data-testid={`schedule-rating-${j.id}`}>
                {Array.from({ length: j.review.rating }).map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                ))}
              </div>
            )}
          </div>
        </div>
        {showActions && ["matched", "active", "in_progress"].includes(j.status) && (
          <div className="mt-4">
            <TeamChat jobId={j.id} currentUserId={user?.id} compact />
          </div>
        )}
      </CardContent>
    </Card>
  );

  const Empty = ({ label }) => (
    <Card><CardContent className="p-10 text-center text-slate-500" data-testid="schedule-empty">{label}</CardContent></Card>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-6">
        <button onClick={() => nav("/cleaner")} className="text-xs font-mono uppercase tracking-widest text-teal-600 hover:text-teal-700 inline-flex items-center gap-1" data-testid="back-to-portal">
          <ArrowLeft className="w-3 h-3" />Back to Portal
        </button>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 mt-2">Active · Schedule</h1>
        <p className="text-sm text-slate-500 mt-1">Your accepted jobs, upcoming schedule, and history.</p>
      </div>

      <Tabs defaultValue="scheduled" data-testid="schedule-tabs">
        <TabsList className="w-full overflow-x-auto flex flex-nowrap justify-start">
          <TabsTrigger value="scheduled" data-testid="tab-scheduled">Scheduled ({matched.length})</TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">In Progress ({active.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Completed ({done.length})</TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="tab-cancelled">Cancelled ({cancelled.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="scheduled" className="mt-6">
          {matched.length === 0 ? <Empty label="No scheduled jobs." /> : matched.map(j => renderRow(j, { showActions: true }))}
        </TabsContent>
        <TabsContent value="active" className="mt-6">
          {active.length === 0 ? <Empty label="No jobs in progress." /> : active.map(j => renderRow(j, { showActions: true }))}
        </TabsContent>
        <TabsContent value="history" className="mt-6">
          {done.length === 0 ? <Empty label="No completed jobs yet." /> : done.map(j => renderRow(j, { showActions: false }))}
        </TabsContent>
        <TabsContent value="cancelled" className="mt-6">
          {cancelled.length === 0 ? <Empty label="No cancelled jobs." /> : cancelled.map(j => renderRow(j, { showActions: false }))}
        </TabsContent>
      </Tabs>

      <Dialog open={!!photoDialog} onOpenChange={(v) => !v && setPhotoDialog(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Camera className="w-5 h-5 text-teal-600" />
              {photoDialog?.phase === "before" ? "Before Photos" : "After Photos"}
            </DialogTitle>
          </DialogHeader>
          {photoDialog && (
            <JobPhotoDocumentation
              jobId={photoDialog.jobId}
              phase={photoDialog.phase}
              mode="capture"
              onReady={(ready) => setPhotoDocReady(ready)}
            />
          )}
          <DialogFooter className="pt-3">
            <Button variant="outline" onClick={() => setPhotoDialog(null)} data-testid="photo-dialog-cancel">Cancel</Button>
            {photoDialog?.phase === "before" ? (
              <Button
                onClick={startJob}
                disabled={!photoDocReady}
                data-testid="photo-dialog-start"
                className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                title={!photoDocReady ? "Please add all required Before photos first" : ""}
              >
                <PlayCircle className="w-4 h-4 mr-1" />Start Cleaning
              </Button>
            ) : (
              <Button
                onClick={completeJob}
                disabled={!photoDocReady}
                data-testid="photo-dialog-complete"
                className="bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50"
                title={!photoDocReady ? "Please add all required After photos first" : ""}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />Mark job completed
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
